import React from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Container,
  Typography,
  Stack,
  Skeleton,
  Button,
} from '@mui/material';
import { DetailCard, CopyButton } from '@marageti/z4-lib';
import { SalesRecording, TripMatch } from '@marageti/z4-sdk/lib/travel';
import { SalesRecordingStatus, SalesRecordingType } from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { CountryCodeMap } from '@marageti/z4-sdk/lib/standards';
import { format } from 'date-fns';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import { DocStatus } from '@marageti/z4-sdk/lib/documents';
import { formatCountryCodes, getCurrencyDetailsContent } from '../../../utils/helpers';
import { formatDateSold, formatInvoiceMonth } from '../../../utils/sales';

const formatDate = (timestamp: string) => format(new Date(timestamp), 'MMM d, yyyy');

type ViewProps = {
  countryCodes: CountryCodeMap | undefined;
  docError: ApiError | undefined;
  isLoading: boolean;
  onClickDownload: () => void;
  salesRecording: SalesRecording | undefined;
  tripMatch: TripMatch | undefined;
};

const SalesRecordingView = ({
  countryCodes,
  docError,
  isLoading,
  onClickDownload,
  salesRecording,
  tripMatch,
}: ViewProps) => {
  const getDetailCardTitle = (salesRecordingType: SalesRecordingType) => {
    const types = {
      [SalesRecordingType.General as string]: 'Sales Details',
      [SalesRecordingType.Adjustment as string]: 'Adjustments',
      [SalesRecordingType.CommissionAfterTravel as string]: 'Sales Details',
      [SalesRecordingType.PlanningFee as string]: 'Sales Details',
      [SalesRecordingType.RefundCredit as string]: 'Adjustments',
    };
    return types[salesRecordingType] || '';
  };

  const getPageTitle = (salesRecordingType: SalesRecordingType) => {
    const types = {
      [SalesRecordingType.General as string]: 'Standard Sale',
      [SalesRecordingType.Adjustment as string]: 'Sales Adjustment',
      [SalesRecordingType.CommissionAfterTravel as string]: 'Commission After Travel',
      [SalesRecordingType.PlanningFee as string]: 'Planning Fee',
      [SalesRecordingType.RefundCredit as string]: 'Refund Credit',
    };
    return types[salesRecordingType] || '';
  };

  const getCardItems = (salesRec: SalesRecording) => {
    const priceItem = {
      title: 'Total Trip Price',
      content: getCurrencyDetailsContent(salesRec.price),
    };

    if (salesRec.salesRecordingType === SalesRecordingType.Adjustment) priceItem.title = 'Changes to Total Trip Price';
    if (salesRec.salesRecordingType === SalesRecordingType.PlanningFee) priceItem.title = 'Amount';

    const commissionItem = {
      title: salesRec.salesRecordingType === SalesRecordingType.General ? 'Commission to Zicasso' : 'Changes to Commission to Zicasso',
      content: getCurrencyDetailsContent(salesRec.commission),
    };
    if (salesRec.salesRecordingType === SalesRecordingType.CommissionAfterTravel) commissionItem.title = 'Commission to Zicasso';
    if (salesRec.salesRecordingType === SalesRecordingType.PlanningFee) commissionItem.title = 'Commission to Zicasso';

    const items = [
      { title: 'Date Recorded', content: formatDateSold(salesRec.createdAt) },
      priceItem,
      commissionItem,
      { title: 'Comments', content: salesRec.priceComments },
      { title: 'Invoice Month', content: formatInvoiceMonth(salesRecording?.invoiceDate!) },
      {
        title: 'Sales ID',
        content: (
          <>
            {salesRec.id}
            <CopyButton textToCopy={salesRec.id} iconFontSize="small" size="small" />
          </>
        ),
      },
      ((salesRec.document) ? (
        <Button
          disabled={salesRec.document.status !== DocStatus.AvailableStatus}
          onClick={onClickDownload}
          variant="contained"
          sx={{ width: 'fit-content' }}
        >
          Download Attachment
        </Button>
      ) : null),
    ];
    return items;
  };

  return (
    <Container
      maxWidth="md"
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Box
        sx={{
          width: '100%',
          py: { desktop: 7, mobile: 3 },
        }}
      >
        <Stack spacing={3}>
          {isLoading && (
            <>
              <Skeleton height={32} width={200} />
              <Skeleton height={252} width={774} />
              <Skeleton height={480} width={774} />
            </>
          )}
          {(!isLoading && tripMatch && salesRecording && countryCodes) && (
            <>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="h2">{getPageTitle(salesRecording.salesRecordingType)}</Typography>
              </Stack>
              {salesRecording.status === SalesRecordingStatus.InvoicedStatus && (
                <Alert severity="error">
                  <AlertTitle>Previously Invoiced</AlertTitle>
                  This recording has already been invoiced, and can no longer be edited. If changes are needed, the partner can submit a sales adjustment for the associated trip.
                </Alert>
              )}
              {salesRecording.status === SalesRecordingStatus.VerifiedStatus && (
                <Alert severity="success">
                  <AlertTitle>Recording Approved</AlertTitle>
                  This recording has already been approved by Zicasso and is locked for invoicing. If changes are needed, Partners can submit a sales adjustment once the status changes to Invoiced.
                </Alert>
              )}
              {docError && (
                <Alert severity="error">
                  <AlertTitle>There was an issue downloading the sales recording</AlertTitle>
                  {docError.message}
                  {docError.code && `Code: ${docError.code} `}
                  {docError.id && `ID: ${docError.id}`}
                </Alert>
              )}
              <DetailCard
                title="Trip Info"
                items={[
                  { title: 'Countries', content: formatCountryCodes(tripMatch.countries, countryCodes) },
                  { title: 'When', content: `${formatDate(tripMatch.departureDate)} (${tripMatch.totalNights} Nights)` },
                  { title: 'Travelers', content: `${tripMatch.numberOfAdults} Adults, ${tripMatch.numberOfChildren} Children` },
                ]}
              />
              <DetailCard
                title={getDetailCardTitle(salesRecording.salesRecordingType)}
                items={getCardItems(salesRecording)}
              />
            </>
          )}
        </Stack>
      </Box>
    </Container>
  );
};

export default SalesRecordingView;
